// Gestion de l'import/export des données

import {
  initDB,
  getAllItems,
  saveItems,
  clearStore,
  importToStore,
} from "../services/dbService";
import { initEventList } from "./EventList";
import { initPeriodSelector } from "./PeriodSelector";
import { updateCalculs } from "../services/calculService";
import { loadCustomColors, applyCustomColors } from "../services/colorService";
import {
  initializeGoogleAPI,
  authenticateUser,
  saveToGoogleDriveWithFolder,
  selectFolder,
  listAppFilesInFolderWithDiagnosis,
  downloadFromGoogleDrive,
  signOutGoogle,
} from "../services/googleDriveService";
import { savePref, loadPref } from "../services/prefsService";

export function initDataManager() {
  console.log('🔧 Initialisation DataManager...');

  const dataBtn = document.getElementById('data-btn');
  const dataModal = document.getElementById('data-modal');
  const closeModalBtn = document.getElementById('close-data-modal');

  if (!dataBtn || !dataModal) {
    console.error('❌ Éléments du DataManager non trouvés');
    return;
  }

  // ✅ INITIALISER LES LISTENERS UNE SEULE FOIS
  initAllModalListeners();

  // ✅ SIMPLE : Juste ouvrir/fermer sans réinitialiser
  dataBtn.addEventListener('click', () => {
    console.log('🎯 Ouverture du modal Import/Export');
    dataModal.style.display = 'flex';
    refreshModalData(); // Juste rafraîchir les données
  });

  closeModalBtn?.addEventListener('click', () => {
    dataModal.style.display = 'none';
  });

  dataModal.addEventListener('click', (e) => {
    if (e.target === dataModal) {
      dataModal.style.display = 'none';
    }
  });

  initDragAndDrop();
  console.log('✅ DataManager initialisé');
}

// ✅ FONCTION UNIQUE : Tous les listeners en une fois
function initAllModalListeners() {
  console.log('⚙️ Initialisation unique de tous les event listeners...');

  // ✅ AJOUTER cette variable locale manquante
  let downloadInProgress = false;

  // 📥 Export avec protection
  attachSingleListener("export-btn", async (e) => {
    if (downloadInProgress) return;

    downloadInProgress = true;
    const btn = e.target;
    const originalText = btn.textContent;

    btn.disabled = true;
    btn.textContent = "⏳ Export...";

    try {
      const data = await exportAllData();
      const customName = document.getElementById("export-name")?.value.trim() || "";

      if (customName) await saveUserName(customName);

      const filename = await generateFileName(customName);
      downloadJson(data, filename);
    } catch (error) {
      console.error("Erreur export:", error);
      alert("Erreur lors de l'export");
    } finally {
      setTimeout(() => {
        downloadInProgress = false;
        btn.disabled = false;
        btn.textContent = originalText;
      }, 2000);
    }
  });

  // 📥 Téléchargement direct
  attachSingleListener("download-btn", async (e) => {
    if (downloadInProgress) return;

    downloadInProgress = true;
    const btn = e.target;
    const originalText = btn.textContent;

    btn.disabled = true;
    btn.textContent = "⏳ Téléchargement...";

    try {
      const data = await exportAllData();
      const filename = await generateFileName();
      downloadJson(data, filename);
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      alert("Erreur lors du téléchargement");
    } finally {
      setTimeout(() => {
        downloadInProgress = false;
        btn.disabled = false;
        btn.textContent = originalText;
      }, 2000);
    }
  });

  // 📁 Import fichier
  attachSingleListener("import-btn", async () => {
    const fileInput = document.getElementById("import-file");
    if (!fileInput.files?.length) {
      alert("Veuillez sélectionner un fichier");
      return;
    }

    try {
      const file = fileInput.files[0];
      const fileContent = await readFileAsJson(file);

      if (!isValidExportFormat(fileContent)) {
        alert("❌ Fichier non valide");
        return;
      }

      if (!confirm("Remplacer toutes vos données ?")) return;

      await importAllDataSilent(fileContent);
      alert("✅ Importation réussie !");
      document.getElementById('data-modal').style.display = 'none';

    } catch (error) {
      console.error("Erreur import:", error);
      alert(`❌ Erreur: ${error.message}`);
    }
  });

  // 📋 Import presse-papiers
  attachSingleListener("paste-import-btn", async () => {
    const textarea = document.getElementById("json-textarea");
    const jsonText = textarea.value.trim();

    if (!jsonText) {
      alert("❌ Collez le JSON dans la zone de texte");
      return;
    }

    try {
      const jsonData = JSON.parse(jsonText);

      if (!isValidExportFormat(jsonData)) {
        alert("❌ Format JSON non valide");
        return;
      }

      if (!confirm("Remplacer toutes vos données ?")) return;

      await importAllDataSilent(jsonData);
      alert("✅ Importation réussie !");
      document.getElementById('data-modal').style.display = 'none';

    } catch (error) {
      console.error("Erreur import presse-papiers:", error);
      alert("❌ JSON invalide");
    }
  });

  // 📧 Email et Gmail
  attachSingleListener("email-btn", () => handleEmailExport(createEmailWithJsonContent));
  attachSingleListener("gmail-btn", () => handleEmailExport(createGmailWithJsonContent));

  // 📋 Copier
  attachSingleListener("copy-btn", async () => {
    try {
      const data = await exportAllData();
      await copyJsonToClipboard(data);
      alert("✅ Copié !");
    } catch (error) {
      console.error("Erreur copie:", error);
      alert("❌ Erreur lors de la copie");
    }
  });

  // ☁️ Google Drive
  attachSingleListener("export-drive-folder-btn", handleDriveExport);
  attachSingleListener("import-drive-btn", handleDriveImport);
  attachSingleListener("sign-out-btn", handleSignOut);

  // 📝 Champs de saisie (avec vérification anti-doublons)
  setupInputListeners();

  console.log('✅ Tous les listeners initialisés');
}

// ✅ UTILITAIRE : Attacher un listener unique
function attachSingleListener(elementId, handler) {
  const element = document.getElementById(elementId);
  if (element) {
    // Marquer pour éviter les doublons
    if (!element.hasAttribute('data-listener-attached')) {
      element.addEventListener('click', handler);
      element.setAttribute('data-listener-attached', 'true');
    }
  }
}

// ✅ UTILITAIRE : Setup des champs de saisie
function setupInputListeners() {
  const nameInput = document.getElementById("export-name");
  if (nameInput && !nameInput.hasAttribute('data-listeners-attached')) {
    nameInput.addEventListener('input', updateFilenamePreview);
    nameInput.addEventListener('blur', async () => {
      const value = nameInput.value.trim();
      if (value) await saveUserName(value);
    });

    // Options de date
    const includeDate = document.getElementById("include-date");
    const includeTime = document.getElementById("include-time");
    const datePositionRadios = document.querySelectorAll('input[name="date-position"]');

    includeDate?.addEventListener('change', () => {
      const dateOptions = document.getElementById("date-options");
      dateOptions?.classList.toggle('disabled', !includeDate.checked);
      updateFilenamePreview();
    });

    includeTime?.addEventListener('change', updateFilenamePreview);
    datePositionRadios.forEach(radio => {
      radio.addEventListener('change', updateFilenamePreview);
    });

    nameInput.setAttribute('data-listeners-attached', 'true');
  }
}

// ✅ SIMPLE : Juste rafraîchir les données sans toucher aux listeners
function refreshModalData() {
  const nameInput = document.getElementById("export-name");
  if (nameInput) {
    loadUserName().then(savedName => {
      if (savedName && !nameInput.value.trim()) {
        nameInput.value = savedName;
        updateFilenamePreview();
      }
    });
  }
  updateFilenamePreview();
}

// ✅ SIMPLE : Download avec protection unique
function downloadJson(data, filename) {
  try {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`✅ Téléchargé: ${filename}`);
  } catch (error) {
    console.error('❌ Erreur téléchargement:', error);
    throw error;
  }
}

// ✅ AJOUTER ces fonctions après downloadJson()
async function saveUserName(name) {
  await savePref('userName', name);
}

async function loadUserName() {
  return await loadPref('userName', '');
}

// ✅ HANDLERS : Fonctions séparées pour les actions complexes
async function handleEmailExport(emailFunction) {
  try {
    const data = await exportAllData();
    const customName = document.getElementById("export-name")?.value.trim() || "";

    if (customName) await saveUserName(customName);

    const filename = await generateFileName(customName);
    emailFunction(data, filename);
  } catch (error) {
    console.error("Erreur email:", error);
    alert("Erreur lors de la préparation de l'email");
  }
}

async function handleDriveExport() {
  try {
    await initializeGoogleAPI();
    await authenticateUser();

    const selectedFolder = await selectFolder();
    if (!selectedFolder) return;

    const data = await exportAllData();
    const customName = document.getElementById("export-name")?.value.trim() || "";

    if (customName) await saveUserName(customName);

    const filename = await generateFileName(customName);
    await saveToGoogleDriveWithFolder(data, filename, selectedFolder.id);

    alert(`✅ Sauvegardé dans "${selectedFolder.name}"`);
    document.getElementById("sign-out-btn").style.display = "block";
  } catch (error) {
    console.error("Erreur Drive export:", error);
    alert("❌ Erreur Google Drive");
  }
}

async function handleDriveImport() {
  try {
    await initializeGoogleAPI();
    await authenticateUser();

    const loadingModal = createLoadingModal("Google Drive");
    document.body.appendChild(loadingModal);
    document.getElementById('data-modal').style.display = 'none';

    try {
      const files = await listAppFilesInFolderWithDiagnosis();

      if (loadingModal.parentNode) {
        loadingModal.parentNode.removeChild(loadingModal);
      }

      if (files.length === 0) {
        alert("📂 Aucun fichier trouvé sur votre Google Drive");
        return;
      }

      await showFolderFilesInSeparateModal({
        id: null,
        name: "Liste des fichiers"
      }, files);

    } catch (loadError) {
      if (loadingModal.parentNode) {
        loadingModal.parentNode.removeChild(loadingModal);
      }
      throw loadError;
    }

    document.getElementById("sign-out-btn").style.display = "block";
  } catch (error) {
    console.error("Erreur Drive import:", error);
    alert("❌ Erreur accès Google Drive");
  }
}

async function handleSignOut() {
  try {
    await signOutGoogle();
    document.getElementById("sign-out-btn").style.display = "none";

    const driveFilesSection = document.getElementById("drive-files-section");
    if (driveFilesSection) driveFilesSection.style.display = "none";

    alert("✅ Déconnecté de Google Drive");
  } catch (error) {
    console.error("Erreur déconnexion:", error);
    alert("❌ Erreur lors de la déconnexion");
  }
}

// Fonction pour lire un fichier en tant que JSON
function readFileAsJson(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);
        resolve(json);
      } catch (error) {
        reject("Le fichier n'est pas un JSON valide");
      }
    };

    reader.onerror = () => {
      reject("Erreur lors de la lecture du fichier");
    };

    reader.readAsText(file);
  });
}

// Formater une date pour le nom de fichier (YYYY-MM-DD) - version simplifiée
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// MODIFIER la fonction pour formater date + heure selon le nouveau format
function formatDateWithTime(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}h${minutes}`;
}

// MODIFIER updateFilenamePreview pour tenir compte des nouvelles options
function updateFilenamePreview() {
  const nameInput = document.getElementById("export-name");
  const preview = document.querySelector(".filename-preview-output");
  const previewIcon = document.querySelector(".preview-icon");
  const includeDate = document.getElementById("include-date");
  const includeTime = document.getElementById("include-time");
  const datePosition = document.querySelector('input[name="date-position"]:checked');

  if (nameInput && preview) {
    const userName = nameInput.value.trim() || "backup-heures-meuthopik";
    const userNameWithDashes = userName.replace(/[\s.]+/g, '-').toLowerCase();

    let filename = userNameWithDashes;

    if (includeDate && includeDate.checked) {
      const dateStr = includeTime && includeTime.checked
        ? formatDateWithTime(new Date())
        : formatDate(new Date());

      if (datePosition && datePosition.value === 'before') {
        filename = `${dateStr}_${userNameWithDashes}`;
      } else {
        filename = `${userNameWithDashes}_${dateStr}`;
      }
    }

    // Animation de l'icône et du texte
    preview.classList.add('updating');
    if (previewIcon) previewIcon.classList.add('active');

    setTimeout(() => {
      preview.textContent = `${filename}.json`;

      setTimeout(() => {
        preview.classList.remove('updating');
        if (previewIcon) previewIcon.classList.remove('active');

      }, 300);
    }, 100);
  }
}

// MODIFIER generateFileName pour utiliser les options
async function generateFileName(baseName = null) {
  const includeDate = document.getElementById("include-date");
  const includeTime = document.getElementById("include-time");
  const datePosition = document.querySelector('input[name="date-position"]:checked');

  // Si un baseName est fourni, l'utiliser
  if (baseName) {
    const baseNameWithDashes = baseName.replace(/[\s.]+/g, '-').toLowerCase();
    return applyDateOptions(baseNameWithDashes, includeDate, includeTime, datePosition);
  }

  // Sinon, essayer de charger depuis le champ ou IndexedDB
  const baseNameInput = document.getElementById("export-name");
  let userBaseName = baseNameInput ? baseNameInput.value.trim() : "";

  // Si pas de nom dans le champ, charger depuis IndexedDB
  if (!userBaseName) {
    userBaseName = await loadUserName();
  }

  const finalBaseName = userBaseName || "backup-heures-meuthopik";
  const finalBaseNameWithDashes = finalBaseName.replace(/[\s.]+/g, '-').toLowerCase();

  return applyDateOptions(finalBaseNameWithDashes, includeDate, includeTime, datePosition);
}

// Nouvelle fonction pour appliquer les options de date
function applyDateOptions(baseName, includeDateEl, includeTimeEl, datePositionEl) {
  if (!includeDateEl || !includeDateEl.checked) {
    return `${baseName}.json`;
  }

  const dateStr = includeTimeEl && includeTimeEl.checked
    ? formatDateWithTime(new Date())
    : formatDate(new Date());

  if (datePositionEl && datePositionEl.value === 'before') {
    return `${dateStr}_${baseName}.json`;
  } else {
    return `${baseName}_${dateStr}.json`;
  }
}

// Fonction d'export
async function exportAllData() {
  try {
    const events = await getAllItems("events");
    const periods = await getAllItems("periods");
    const customColors = await getAllItems("customColors");
    const salaires = await getAllItems("salaires");

    // S'assurer que toutes les données sont sérialisables
    const exportData = {
      events: Array.isArray(events) ? events : [],
      periods: Array.isArray(periods) ? periods : [],
      customColors: Array.isArray(customColors) ? customColors : [],
      salaires: Array.isArray(salaires) ? salaires : [],
      exportDate: new Date().toISOString(),
      version: "1.0",
      // AJOUTER : Informations de build pour traçabilité
      buildInfo: {
        devFolderName: window.APP_BUILD_INFO?.devFolderName || 'unknown',
        buildTimestamp: window.APP_BUILD_INFO?.buildTimestamp || 'unknown',
        exportEnvironment: window.APP_BUILD_INFO?.environment || 'unknown'
      }
    };

    // Tester la sérialisation
    try {
      JSON.stringify(exportData);
    } catch (serializeError) {
      console.error("Erreur de sérialisation des données:", serializeError);
      throw new Error("Les données ne peuvent pas être exportées (problème de sérialisation)");
    }

    return exportData;
  } catch (error) {
    console.error("Erreur lors de l'export:", error);
    throw error;
  }
}

/**
 * Importer toutes les données depuis un objet JSON (VERSION SILENCIEUSE)
 */
async function importAllData(data) {
  try {
    console.log("📥 Import des données...", data);

    const db = await initDB();
    const storeNames = ['events', 'periods', 'customColors', 'prefs', 'salaires'];

    // CORRECTION : Vider les stores avant l'import pour éviter les conflits de clés
    for (const storeName of storeNames) {
      if (data[storeName] && data[storeName].length > 0) {
        await clearStore(db, storeName);
        await importToStore(db, storeName, data[storeName]);
      }
    }

    // Actualiser l'interface après l'import
    await refreshInterface();

    return true;
  } catch (error) {
    console.error("❌ Erreur lors de l'import:", error);
    throw error;
  }
}

/**
 * Version silencieuse d'importAllData (sans alertes internes)
 */
async function importAllDataSilent(data) {
  try {
    console.log("📥 Import silencieux des données...", data);

    const db = await initDB();
    const storeNames = ['events', 'periods', 'customColors', 'prefs', 'salaires'];

    // Vider les stores avant l'import
    for (const storeName of storeNames) {
      if (data[storeName] && data[storeName].length > 0) {
        await clearStore(db, storeName);
        await importToStore(db, storeName, data[storeName]);
      }
    }

    // Actualiser l'interface
    await refreshInterface();

    return true;
  } catch (error) {
    console.error("❌ Erreur lors de l'import silencieux:", error);
    // NE PAS faire d'alert ici, laisser la fonction appelante gérer
    throw error;
  }
}

async function copyJsonToClipboard(data) {
  try {
    const jsonContent = JSON.stringify(data);
    await navigator.clipboard.writeText(jsonContent);

    return true;
  } catch (error) {
    console.error("Erreur lors de la copie:", error);
    alert("❌ Impossible de copier dans le presse-papiers. Utilisez le téléchargement direct.");
    return false;
  }
}

// Fonction silencieuse pour copier sans alerte
async function copyJsonToClipboardSilent(data) {
  try {
    // JSON compact sur une seule ligne
    const jsonContent = JSON.stringify(data);
    await navigator.clipboard.writeText(jsonContent);
    return true;
  } catch (error) {
    console.error("Erreur lors de la copie silencieuse:", error);
    return false;
  }
}

/**
 * Créer un email avec le contenu JSON
 */
function createEmailWithJsonContent(data, filename) {
  // JSON compact sur une seule ligne
  const jsonString = JSON.stringify(data);
  const subject = encodeURIComponent(`🤙🏾 Sauvegarde - ${filename}`);
  const body = encodeURIComponent(`💡 Comment importer ces données par la suite :
1. Copie le JSON ci-dessous (tout sur une ligne)
2. Va sur https://heures.meuthopik.com > Import/Export > Importer le presse-papiers
3. Colle et clique "Importer"


_______________________________________________


${jsonString}`);

  const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
  window.open(mailtoLink, '_blank');
}

/**
 * Créer un Gmail avec le contenu JSON dans le presse-papiers
 */
async function createGmailWithJsonContent(data, filename) {
  try {
    const jsonString = JSON.stringify(data);
    await navigator.clipboard.writeText(jsonString);

    const subject = encodeURIComponent(`🤙🏾 Sauvegarde - ${filename}`);
    const body = encodeURIComponent(`📋 JSON copié dans le presse-papiers - le coller ci-dessous

    Cette fenêtre simplifiée est la seule possibilité pour automatiser les tâches depuis heures.meuthopik.com
    Tu dois la refermer manuellement après avoir envoyé le mail.
    Tu retrouveras ce mail sur ton compte gmail

💡 Pour importer ces données par la suite :
→ heures.meuthopik.com → Import/Export → Presse-papiers

JSON :



`);

    // ✅ Cette URL fonctionne avec pré-remplissage
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');

  } catch (error) {
    console.error('Erreur:', error);
  }
}

// MODIFIER la fonction showFolderFilesInSeparateModal pour inclure une roue de chargement
async function showFolderFilesInSeparateModal(folder, preloadedFiles = null) {
  try {
    console.log(`📂 Ouverture de la liste des fichiers pour le dossier: ${folder.name}`);

    // SÉCURISÉ : Nettoyer toutes les modales existantes
    const existingModal = document.getElementById('folder-files-modal');
    if (existingModal && document.body.contains(existingModal)) {
      console.log('🧹 Suppression de la modal des fichiers existante');
      document.body.removeChild(existingModal);
    }

    const existingLoadingModal = document.getElementById('loading-modal');
    if (existingLoadingModal && document.body.contains(existingLoadingModal)) {
      console.log('🧹 Suppression du spinner existant');
      document.body.removeChild(existingLoadingModal);
    }

    // Créer la modal avec spinner en premier
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.id = 'folder-files-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1001;
    `;

    const modalContent = document.createElement('div');
    modalContent.className = 'modal-content';
    modalContent.style.cssText = `
      background: white;
      padding: 30px;
      border-radius: 12px;
      max-width: 800px;
      width: 90vw;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 10px 40px rgba(0,0,0,0.3);
      min-height: 200px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `;

    // AFFICHER LE SPINNER EN PREMIER
    modalContent.innerHTML = `
      <div class="loading-container" style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 150px;
      ">
        <div class="spinner" style="
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid var(--primary-color, #3498db);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        "></div>
        <p style="margin: 0; color: #666; font-size: 16px;">
          🔍 Chargement de la liste des fichiers...
        </p>
        <small style="color: #999; margin-top: 10px;">
          Veuillez patienter
        </small>
      </div>
    `;

    // Ajouter l'animation CSS pour le spinner
    if (!document.getElementById('spinner-styles')) {
      const spinnerStyles = document.createElement('style');
      spinnerStyles.id = 'spinner-styles';
      spinnerStyles.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(spinnerStyles);
    }

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // SÉCURISÉ : Setup des event listeners pour fermer pendant le chargement
    let escapeHandler = null;
    const cleanup = () => {
      console.log('🧹 Nettoyage complet de la modal des fichiers');

      if (escapeHandler) {
        document.removeEventListener('keydown', escapeHandler);
        escapeHandler = null;
      }

      if (modal && document.body.contains(modal)) {
        document.body.removeChild(modal);
      }

      // SÉCURISÉ : Nettoyer aussi le spinner au cas où
      const loadingModal = document.getElementById('loading-modal');
      if (loadingModal && document.body.contains(loadingModal)) {
        console.log('🧹 Nettoyage du spinner pendant cleanup');
        document.body.removeChild(loadingModal);
      }
    };

    // Permettre la fermeture pendant le chargement
    escapeHandler = (e) => {
      if (e.key === 'Escape') {
        cleanup();
      }
    };
    document.addEventListener('keydown', escapeHandler);

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        cleanup();
      }
    });

    // CHARGER LES FICHIERS EN ARRIÈRE-PLAN
    let files;
    try {
      // Récupérer les fichiers du dossier (sauf s'ils sont déjà fournis)
      files = preloadedFiles || await listAppFilesInFolderWithDiagnosis(folder.id);

      // Nettoyer toute liste de fichiers existante dans la modal data
      const driveFilesSection = document.getElementById("drive-files-section");
      if (driveFilesSection) {
        driveFilesSection.style.display = "none";
        driveFilesSection.innerHTML = "";
      }
    } catch (loadError) {
      cleanup();
      console.error('Erreur lors du chargement des fichiers:', loadError);

      // Afficher l'erreur dans la modal
      modalContent.innerHTML = `
        <div style="text-align: center; padding: 20px;">
          <h2 style="margin: 0 0 20px 0; color: #e74c3c;">❌ Erreur de chargement</h2>
          <p style="color: #666; margin-bottom: 20px;">
            Impossible de charger les fichiers du dossier "${folder.name}".
          </p>
          <p style="color: #999; font-size: 14px; margin-bottom: 20px;">
            ${loadError.message}
          </p>
          <button id="close-error-modal" class="btn-secondary" style="
            padding: 10px 20px;
            background: #666;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
          ">
            Fermer
          </button>
        </div>
      `;

      modalContent.querySelector('#close-error-modal').addEventListener('click', cleanup);
      return; // Sortir de la fonction
    }

    // CONSTRUIRE LE CONTENU FINAL APRÈS CHARGEMENT
    let filesHtml = '';

    if (files.length === 0) {
      filesHtml = `
        <div style="text-align: center; padding: 20px; color: #666;">
          <p>📂 Le dossier "${folder.name}" ne contient aucun fichier de sauvegarde.</p>
          <p>Votre fichier vient d'être sauvegardé et devrait apparaître ici prochainement.</p>
        </div>
      `;
    } else {
      filesHtml = files.map(file => `
        <div class="drive-file-item" style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          margin-bottom: 10px;
          background: #f9f9f9;
        ">
          <div class="file-info" style="flex-grow: 1;">
            <strong style="display: block; margin-bottom: 5px;">${file.name}</strong>
            <small style="color: #666;">
              Modifié le: ${new Date(file.modifiedTime).toLocaleString()}
              ${file.fullPath ? `<br>Chemin: <span class="file-path">${file.fullPath}</span>` : ''}
            </small>
          </div>
          <button class="btn-action btn-small" onclick="importFromDriveAndCloseModal('${file.id}', '${file.name}')" style="
            margin-left: 15px;
            padding: 8px 15px;
            background: var(--primary-color);
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
          ">
            Importer
          </button>
        </div>
      `).join('');
    }

    // REMPLACER LE CONTENU DU SPINNER PAR LE CONTENU FINAL
    modalContent.style.justifyContent = 'flex-start'; // Réinitialiser le centrage
    modalContent.innerHTML = `
      <div style="text-align: center; margin-bottom: 25px;">
        <h2 style="margin: 0; color: #333;">📁 Trouvés dans le Drive</h2>
        <p style="margin: 10px 0 0 0; color: #666;">${files.length} fichier(s) de sauvegarde trouvé(s)</p>
      </div>

      <div class="folder-files-list" style="margin-bottom: 20px;">
        ${filesHtml}
      </div>

      <div style="text-align: center; border-top: 1px solid #e0e0e0; padding-top: 20px;">
        <button id="refresh-folder-files" class="btn-action" style="
          margin-right: 10px;
          padding: 10px 20px;
          background: #2196F3;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        ">
          🔄 Actualiser
        </button>
        <button id="close-folder-modal" class="btn-secondary" style="
          padding: 10px 20px;
          background: #666;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        ">
          Fermer
        </button>
      </div>
    `;

    // RÉASSIGNER LES EVENT LISTENERS APRÈS LE CHANGEMENT DE CONTENU
    modalContent.querySelector('#close-folder-modal').addEventListener('click', cleanup);

    modalContent.querySelector('#refresh-folder-files').addEventListener('click', async () => {
      try {
        cleanup();
        await showFolderFilesInSeparateModal(folder, null); // Forcer le rechargement avec spinner
      } catch (error) {
        console.error('Erreur lors de l\'actualisation:', error);
        alert('Erreur lors de l\'actualisation des fichiers');
      }
    });

  } catch (error) {
    console.error('❌ Erreur lors de l\'affichage des fichiers du dossier:', error);
    alert(`❌ Impossible d'afficher les fichiers du dossier "${folder.name}"`);
  }
}

// NOUVELLE fonction qui ferme automatiquement la modal après import
window.importFromDriveAndCloseModal = async function(fileId, fileName) {
  try {
    // UNE SEULE CONFIRMATION POUR GOOGLE DRIVE AUSSI
    if (!confirm(`Importer le fichier "${fileName}" ?\n\nCette action remplacera toutes vos données actuelles.`)) {
      return;
    }

    console.log("📥 Téléchargement du fichier depuis Google Drive...");
    const fileContent = await downloadFromGoogleDrive(fileId);

    console.log("📄 Contenu du fichier:", typeof fileContent, fileContent);

    let jsonData;

    // Gérer les différents types de retour de l'API Google Drive
    if (typeof fileContent === 'string') {
      try {
        jsonData = JSON.parse(fileContent);
      } catch (parseError) {
        console.error("Erreur de parsing JSON:", parseError);
        console.error("Contenu problématique:", fileContent.substring(0, 200));
        throw new Error(`Le fichier ne contient pas du JSON valide: ${parseError.message}`);
      }
    } else if (typeof fileContent === 'object' && fileContent !== null) {
      jsonData = fileContent;
    } else {
      throw new Error(`Le contenu du fichier n'est pas valide. Type reçu: ${typeof fileContent}`);
    }

    // VALIDATION AVANT IMPORT
    if (!isValidExportFormat(jsonData)) {
      alert("❌ Le fichier sélectionné n'est pas une sauvegarde valide de Prévisionnel Heures");
      return;
    }

    console.log("💾 Import des données...");
    await importAllData(jsonData);

    // AMÉLIORER : Fermer toutes les modales de fichiers potentielles
    const folderModal = document.getElementById('folder-files-modal');
    if (folderModal) {
      console.log('🧹 Fermeture de la modal des fichiers après import');
      document.body.removeChild(folderModal);
    }

    const loadingModal = document.getElementById('loading-modal');
    if (loadingModal && document.body.contains(loadingModal)) {
      console.log('🧹 Fermeture du spinner après import');
      document.body.removeChild(loadingModal);
    }

    // UNE SEULE ALERTE DE SUCCÈS
    alert(`✅ Importation du fichier "${fileName}" réussie !`);

  } catch (error) {
    console.error("❌ Erreur lors de l'import depuis Google Drive:", error);
    alert(`❌ Erreur lors de l'import du fichier "${fileName}".\n\nDétails: ${error.message}`);
  }
};

// Ajouter cette fonction après importAllData()

function isValidExportFormat(data) {
  // Vérifier que l'objet a la structure attendue
  return (
    data &&
    typeof data === 'object' &&
    Array.isArray(data.events) &&
    (data.periods === undefined || Array.isArray(data.periods)) &&
    (data.customColors === undefined || Array.isArray(data.customColors))
  );
}

// Ajouter cette fonction

async function refreshInterface() {
  try {
    // Recharger la liste des événements
    await initEventList();

    // Recharger le sélecteur de période
    await initPeriodSelector();

    // Recalculer les totaux
    await updateCalculs();

    // CORRIGER : Charger les couleurs avant de les appliquer
    const customColors = await loadCustomColors();
    applyCustomColors(customColors);

    console.log("✅ Interface rechargée avec succès");
  } catch (error) {
    console.error("❌ Erreur lors du rechargement de l'interface:", error);
  }
}

// CORRIGER createLoadingModal pour éviter les doublons
function createLoadingModal(folderName) {
  // SÉCURISÉ : Vérifier et supprimer tout modal existant
  const existingLoadingModal = document.getElementById('loading-modal');
  if (existingLoadingModal && document.body.contains(existingLoadingModal)) {
    console.log('🧹 Suppression du modal de chargement existant');
    document.body.removeChild(existingLoadingModal);
  }

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'loading-modal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  `;

  const modalContent = document.createElement('div');
  modalContent.className = 'modal-content';
  modalContent.style.cssText = `
    background: white;
    padding: 40px;
    border-radius: 12px;
    max-width: 400px;
    width: 90vw;
    text-align: center;
    box-shadow: 0 10px 40px rgba(0,0,0,0.3);
  `;

  modalContent.innerHTML = `
    <div class="spinner" style="
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid var(--primary-color, #3498db);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px auto;
    "></div>
    <p style="margin: 0; color: #666; font-size: 16px;">
      🔍 Chargement des fichiers de backup trouvés dans "<strong>${folderName}</strong>"...
    </p>
    <small style="color: #999; margin-top: 10px; display: block;">
      Veuillez patienter
    </small>
  `;

  modal.appendChild(modalContent);
  return modal;
}

function initDragAndDrop() {
  const dropZone = document.getElementById('drop-zone');
  const fileInput = document.getElementById('import-file');
  const selectedFileInfo = document.getElementById('selected-file-info');
  const fileNameSpan = selectedFileInfo?.querySelector('.file-name');
  const removeBtn = selectedFileInfo?.querySelector('.remove-file');

  if (!dropZone || !fileInput) return;

  let selectedFile = null;

  // Clic sur la zone = ouvrir le sélecteur
  dropZone.addEventListener('click', () => fileInput.click());

  // Événements drag & drop
  dropZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropZone.classList.add('drag-over');
  });

  dropZone.addEventListener('dragleave', () => {
    dropZone.classList.remove('drag-over');
  });

  dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropZone.classList.remove('drag-over');

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        selectFile(file);
      } else {
        alert('Veuillez déposer un fichier JSON valide');
      }
    }
  });

  // Sélection via input file
  fileInput.addEventListener('change', (e) => {
    if (e.target.files.length > 0) {
      selectFile(e.target.files[0]);
    }
  });

  // Fonction pour sélectionner un fichier
  function selectFile(file) {
    selectedFile = file;
    fileInput.files = createFileList(file);

    // Afficher les infos du fichier
    if (fileNameSpan) fileNameSpan.textContent = file.name;
    if (selectedFileInfo) selectedFileInfo.style.display = 'flex';

    // Masquer le texte par défaut
    const dropText = dropZone.querySelector('.drop-text');
    const dropIcon = dropZone.querySelector('.drop-icon');
    if (dropText) dropText.style.display = 'none';
    if (dropIcon) dropIcon.style.display = 'none';

    dropZone.classList.add('has-file');
  }

  // Bouton de suppression
  if (removeBtn) {
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      clearSelection();
    });
  }

  function clearSelection() {
    selectedFile = null;
    fileInput.value = '';

    // Masquer les infos du fichier
    if (selectedFileInfo) selectedFileInfo.style.display = 'none';

    // Réafficher le texte par défaut
    const dropText = dropZone.querySelector('.drop-text');
    const dropIcon = dropZone.querySelector('.drop-icon');
    if (dropText) dropText.style.display = 'block';
    if (dropIcon) dropIcon.style.display = 'block';

    dropZone.classList.remove('has-file');
  }

  // Fonction utilitaire pour créer une FileList
  function createFileList(file) {
    const dt = new DataTransfer();
    dt.items.add(file);
    return dt.files;
  }
}


